"use strict";

// Status and Submit button for the signed-in QA reviewer on the QA Review page.
// Opening the page marks the reviewer's review of this language as started.
// Once submitted, the review is closed to the reviewer: submitting sends them
// to their dashboard, and reopening it (a link, an old tab) shows a blocking
// notice over the page until an admin returns it. Users who are not QA
// reviewers on the project (admins, for instance) see nothing.
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { startQAReview, submitQAReview } from "./api";
import {
    QA_STATUS,
    QA_STATUS_COLORS,
    formatQADate,
    qaStatusLabel,
} from "./status";

const QASubmitBar = ({ projectId, language, languageName }) => {
    const [review, setReview] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!projectId || !language) return undefined;
        let cancelled = false;
        setReview(null);

        startQAReview(projectId, language)
            .then(({ data }) => {
                if (!cancelled) {
                    setReview({ status: data.status, ...(data.review || {}) });
                }
            })
            .catch((error) => {
                // 403: not a QA reviewer here, so there is nothing to submit.
                if (error?.response?.status !== 403) {
                    console.error("Unable to load the QA review status", error);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [projectId, language]);

    if (!review) return null;

    const status = review.status || QA_STATUS.AVAILABLE;
    const submitted = status === QA_STATUS.SUBMITTED;

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await submitQAReview(projectId, language);
            setConfirmOpen(false);
            navigate("/landing", {
                state: { qaSubmitted: languageName || language },
            });
        } catch (error) {
            toast.error(error?.response?.data?.error || "Unable to submit the review");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Stack spacing={1} sx={{ mb: 1 }}>
            {status === QA_STATUS.RETURNED && (
                <Alert severity="warning">
                    An admin returned this review
                    {review.returnNote ? `: ${review.returnNote}` : "."}
                </Alert>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip
                    size="small"
                    label={`QA review: ${qaStatusLabel(status)}`}
                    color={QA_STATUS_COLORS[status]}
                />
                {!submitted && (
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => setConfirmOpen(true)}
                    >
                        Submit review
                    </Button>
                )}
            </Stack>

            {/* No close action: the only way on is back to the dashboard. */}
            <Dialog open={submitted} disableEscapeKeyDown>
                <DialogTitle>You submitted this review</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You submitted your {languageName || language} review
                        {review.submittedAt ? ` on ${formatQADate(review.submittedAt)}` : ""}.
                        It stays closed unless an admin returns it to you.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => navigate("/landing")}>
                        Back to my projects
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)}>
                <DialogTitle>Submit your {languageName || language} review?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This tells the admins you have finished. The review is removed
                        from your dashboard and closed to you until an admin returns it.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default QASubmitBar;
